﻿import { FormatServiceLookupEditorOptions, FormatServiceLookupPickerBase } from "@/Data/List/FormatServiceLookupEditor";
import { Criteria, Decorators, EntityDialog, Fluent, GridRowSelectionMixin, IGetEditValue, IReadOnly, ISetEditValue, IStringValue, ListRequest, localText, notifyWarning, PropertyDialog, resolveUrl, TemplatedDialog, trimToNull, tryGetText, tryGetWidget, Widget, EditorProps, WidgetProps, GridPersistanceFlags } from "@serenity-is/corelib";
import { SiteEntityGrid } from "@/Common/Base/SiteEntityGrid";
import { parseCriteria } from "@/Common/CriteriaExpression/CriteriaExpression";
import { microFormat } from "@/Common/Helpers/MicroFormat";

export interface DynamicPickerOptions extends FormatServiceLookupEditorOptions {
    entityType?: string;
    criteriaText?: string;
    multiple?: boolean;
    service?: string;
    columnsKey?: string;
    delimited?: boolean;
    idField: string;
    textField: string;
    resultFormat?: string;
    selectionFormat?: string;
    textFormat?: string;
}

@Decorators.registerEditor("Matrix.Data.DynamicPicker", [IReadOnly, IGetEditValue, ISetEditValue, IStringValue])
@Decorators.element('<input type="text"/>')
export class DynamicPicker extends FormatServiceLookupPickerBase<DynamicPickerOptions, any>
    implements IReadOnly, IGetEditValue, ISetEditValue, IStringValue {

    private ownerWidget: EntityDialog<any, any> | PropertyDialog<any, any> | null = null;
    private formattedCriteria: string | null = null;

    constructor(options: EditorProps<DynamicPickerOptions>) {
        options.columnsKey ??= options.entityType;
        options.entityType ??= options.columnsKey;

        super(options);
        this.setInplaceVisibility();
        this.tryFindOwnerDialog();
    }

    private setInplaceVisibility() {
        if (!this.options.columnsKey?.length || !this.options.service?.length) {
            this.inplaceButton.hide();
        }
    }

    protected getFilterCriteria(): null | any[] {
        return Criteria.and(super.getFilterCriteria(), this.getCriteriaFromParams())
    }

    handleEditClick() {
        const dlg = new DynamicPickerDialog({
            entityType: this.options.entityType,
            columnsKey: this.options.columnsKey,
            service: this.options.service,
            idField: this.options.idField,
            multiple: this.options.multiple,
            criteria: this.getFilterCriteria(),
            criteriaText: this.formattedCriteria
        });

        dlg.value = this.values;

        dlg.dialogOpen();
        dlg.onSuccess = sel => {
            this.values = sel.map(x => x[this.options.idField]);
            return true;
        }
    }

    private tryFindOwnerDialog() {
        if (this.domNode?.id == null)
            return;

        let owner = this.domNode.parentElement;
        while (owner != null) {
            const ownerWidget = tryGetWidget(owner, Widget);
            if (ownerWidget != null && ownerWidget instanceof EntityDialog || ownerWidget instanceof PropertyDialog) {
                this.ownerWidget = ownerWidget;
                break;
            }

            owner = owner.parentElement;
        }
    }

    private getCriteriaFromParams() {
        if (this.options.criteriaText == null || this.options.criteriaText.length == 0)
            return null;

        const data =  {
            ...this.ownerWidget?.["get_entity"]?.(),
            ...this.ownerWidget?.["getSaveEntity"]?.()
        };

        const formattedText = microFormat(this.options.criteriaText, data, {
            handler: (token, data, _offset, _str) => {
                const value = data[token];
                if (value == null)
                    return "null";

                if (typeof value == "string")
                {
                    if (value.length == 0)
                        return "null";

                    return "'" + value.toString().replace("'", "''") + "'";
                }

                if (typeof value == "number")
                    return value.toString();

                if (typeof value == "boolean")
                    return value ? "1" : "0";

                return "'" + value.toString().replace("'", "''") + "'";
            }
        });

        try {
            const replaceNulls = [];

            const visit = (criteria: any[]) => {
                if (criteria.length == 3 && Array.isArray(criteria[2]) && criteria[2][0] == "null")
                {
                    const op = criteria[1];
                    replaceNulls.push({
                        field: criteria[0][0],
                        op
                    });

                    if (op == "=" || op == "<=")
                        return ["is null", criteria[0]];

                    if (op == "!=" || op == "<>" || op == ">")
                        return ["is not null", criteria[0]];

                    if (op == ">=")
                        return ["1", "=", "1"];

                    if (op == "<")
                        return ["1", "=", "0"];

                    return [criteria[0], criteria[1], null];
                }

                return criteria.map(x => Array.isArray(x) ? visit(x) : x);
            }

            this.formattedCriteria = formattedText;
            return visit(parseCriteria(formattedText));
        } catch (e) {
            this.formattedCriteria = null;
            return null;
        }
    }
}

interface DynamicPickerDialogGridOptions {
    entityType: string;
    columnsKey: string;
    service: string;
    idField: string;
    multiple: boolean;
    criteria?: any[];
    criteriaText?: string;
}

class DynamicPickerDialog extends TemplatedDialog<DynamicPickerDialogGridOptions> {
    private pickerGrid: DynamicPickerGrid;
    private readonly singularName: string;
    private readonly pluralName: string;

    constructor(options: WidgetProps<DynamicPickerDialogGridOptions>) {
        super(options);

        this.pickerGrid = new DynamicPickerGrid({
            ...options,
            element: this.byId("PickerGrid")
        });

        this.singularName = tryGetText(`Db.${options.entityType}.EntitySingular`) ?? "Item";
        this.pluralName = tryGetText(`Db.${options.entityType}.EntityPlural`) ?? "Items";
        this.dialogTitle = `Select ${!options.multiple ? this.singularName : this.pluralName}`;
    }

    protected getTemplate() {
        const availHeight = window.innerHeight - 150;
        const pickerHeight = Math.max(300, Math.min(availHeight, 600));

        return `<div><div id="~_PickerGrid" style="height:${pickerHeight}px"></div></div>`;
    }

    protected getDialogOptions() {
        const opt = super.getDialogOptions();
        opt.buttons = [
            {
                text: localText("Dialogs.OkButton", "OK"),
                click: () => {
                    const selected = this.pickerGrid.selectedItems;
                    if (!selected.length) {
                        if (!this.options.multiple)
                            notifyWarning(`Please select one ${this.singularName.toLowerCase()}!`);
                        else
                            notifyWarning(`Please select some ${this.pluralName.toLowerCase()}!`);

                        return;
                    }

                    if (!this.options.multiple && selected.length > 1) {
                        notifyWarning(`Please select only one ${this.singularName.toLowerCase()}!`);
                        return;
                    }

                    if (!this.onSuccess || this.onSuccess(selected))
                        this.dialogClose();
                }
            },
            {
                text: localText("Dialogs.CancelButton", "Cancel"),
                click: () => {
                    this.dialogClose();
                }
            }
        ];

        return opt;
    }

    get selectedItems() {
        return this.pickerGrid.selectedItems;
    }

    get value() {
        return this.pickerGrid.value;
    }

    set value(value: string[]) {
        this.pickerGrid.value = value;
    }

    public onSuccess: (selected: any[]) => boolean;
}

@Decorators.filterable()
class DynamicPickerGrid extends SiteEntityGrid<any, DynamicPickerDialogGridOptions> {
    protected getColumnsKey() { return this.options.columnsKey; }
    protected getDialogType() { return null; }
    protected getIdProperty() { return this.options.idField; }
    protected getLocalTextPrefix() { return this.options.columnsKey; }
    protected getService() { return ""; }

    private readonly rowSelection: GridRowSelectionMixin;

    constructor(options: WidgetProps<DynamicPickerDialogGridOptions>) {
        super(options);

        this.rowSelection = new GridRowSelectionMixin(this);

        if (!options.multiple) {
            Fluent(this.domNode).on("mousedown", (e) => {
                if (!(e.target instanceof HTMLElement))
                    return;

                if (!e.target.closest('.select-item'))
                    return;

                const cell = this.slickGrid.getCellFromEvent(e);
                const item = this.view.getItem(cell.row);

                const selectedKeys = this.rowSelection.getSelectedKeys();

                if (selectedKeys.length == 0)
                    return;

                if (selectedKeys[0] == item[this.options.idField] + "")
                    return;

                this.rowSelection.clear();
            });
        }

        this.element.on("click", ".clear-selection", (e) => {
            if (e.target.closest(".clear-selection") == null)
                return;

            this.rowSelection.clear();
            this.slickGrid.invalidateAllRows();
            this.slickGrid.render();
        });

        this.showCriteria();
    }

    protected showCriteria() {
        if (trimToNull(this.options.criteriaText) == null)
            return;

        Fluent('div')
            .class('dynamic-criteria-info')
                .append(Fluent('i').class('fa fa-filter'))
                    .append(Fluent('em').text(this.options.criteriaText))
            .appendTo(this.toolbar.domNode);
    }

    protected persistSettings(flags?: GridPersistanceFlags) {
        flags ??= {};
        flags.filterItems = false;
        flags.quickFilters = false;
        flags.quickFilterText = false;

        super.persistSettings(flags);
    }

    protected getPersistanceKey(): string {
        return `${super.getPersistanceKey()}:${this.options.entityType}`;
    }

    protected useQueryCriteria(): any {
        return false;
    }

    protected getColumns() {
        const columns = super.getColumns();
        const selectionColumn = GridRowSelectionMixin.createSelectColumn(() => this.rowSelection);

        if (!this.options.multiple)
            selectionColumn.name = '<span class="clear-selection" style="border: 1px solid var(--s-input-border);background-color: var(--s-input-bg);"><i class="fa fa-eraser p-1"></i></span>';

        columns.splice(0, 0, selectionColumn);
        return columns;
    }

    protected getInitialTitle() {
        return null;
    }

    protected getViewOptions() {
        const opt = super.getViewOptions();
        opt.rowsPerPage = 50;
        opt.url = resolveUrl(this.options.service);
        return opt;
    }

    protected getButtons() {
        return super.getButtons().filter(x => x.cssClass != 'add-button');
    }

    get selectedItems() {
        return this.rowSelection.getSelectedKeys().map(x => this.view.getItemById(x));
    }

    get value() {
        return this.rowSelection.getSelectedKeys();
    }

    set value(value: string[]) {
        this.rowSelection.clear();
        this.rowSelection.setSelectedKeys(value || []);
    }

    protected onViewSubmit() {
        if (!super.onViewSubmit())
            return false;

        const request = this.view.params as ListRequest;
        if (this.options.criteria != null)
            request.Criteria = Criteria.and(request.Criteria, this.options.criteria);

        return true;
    }
}